<template>
  <MGuidedCheckoutStep
    :step-number="stepNumber"
    title="Wähle das passende Zubehör."
    description="Wähle die passende Gruppe und stelle die gewünschten Mengen ein."
  >
    <p
      v-if="isLoading"
      class="m-gc-step-accessories__status"
    >
      Zubehör wird geladen…
    </p>

    <p
      v-else-if="!groupedProducts.length"
      class="m-gc-step-accessories__status"
    >
      Aktuell ist kein Zubehör mit diesem Produkt verknüpft.
    </p>

    <template v-else>
      <div class="m-gc-step-accessories__filters">
        <button
          v-for="group in groupedProducts"
          :key="group.id"
          type="button"
          class="m-gc-step-accessories__filter"
          :class="{ 'm-gc-step-accessories__filter--active': activeGroupId === group.id }"
          @click="activeGroupId = group.id"
        >
          {{ group.label }}
          <span
            v-if="getGroupSelectedCount(group) > 0"
            class="m-gc-step-accessories__filter-count"
          >
            {{ getGroupSelectedCount(group) }}
          </span>
        </button>
      </div>

      <p
        v-if="!visibleProducts.length"
        class="m-gc-step-accessories__status"
      >
        In dieser Gruppe ist aktuell kein Zubehör verknüpft.
      </p>

      <div
        v-else
        class="m-gc-step-accessories__items"
      >
        <div
          v-for="item in visibleProducts"
          :key="item.sku"
          class="m-gc-step-accessories__item"
          :class="{ 'm-gc-step-accessories__item--active': getQty(item.sku) > 0 }"
        >
          <div class="m-gc-step-accessories__item-main">
            <SfImage
              :src="getThumbnailForProduct(item)"
              :alt="item.name"
              width="56"
              height="56"
            />
            <div class="m-gc-step-accessories__item-content">
              <div class="m-gc-step-accessories__item-title-row">
                <button
                  type="button"
                  class="m-gc-step-accessories__item-name"
                  @click="openProductDetails(item)"
                >
                  {{ item.name }}
                </button>
                <button
                  type="button"
                  class="m-gc-step-accessories__item-info"
                  :aria-label="'Details ansehen: ' + item.name"
                  @click="openProductDetails(item)"
                >
                  <img
                    src="/assets/custom/icons/icon-info-dark.svg"
                    alt=""
                    width="14"
                    height="14"
                  >
                </button>
              </div>
              <span class="m-gc-step-accessories__item-price">
                {{ formatUnitPrice(item) }}
              </span>
            </div>
          </div>

          <div class="m-gc-step-accessories__qty">
            <button
              type="button"
              class="m-gc-step-accessories__qty-btn"
              :disabled="getQty(item.sku) <= 0"
              :aria-label="'Menge verringern: ' + item.name"
              @click="changeQty(item, -1)"
            >
              −
            </button>
            <input
              :id="'acc-qty-' + item.sku"
              type="number"
              min="0"
              step="1"
              class="m-gc-step-accessories__qty-input"
              :value="getQty(item.sku)"
              :aria-label="'Menge: ' + item.name"
              @input="setQty(item, $event.target.value)"
            >
            <button
              type="button"
              class="m-gc-step-accessories__qty-btn"
              :aria-label="'Menge erhöhen: ' + item.name"
              @click="changeQty(item, 1)"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </template>
  </MGuidedCheckoutStep>
</template>

<script>
import { mapActions } from 'vuex'
import { SfImage } from '@storefront-ui/vue'
import { getThumbnailForProduct } from '@vue-storefront/core/modules/cart/helpers'
import { prepareRelatedQuery } from '@vue-storefront/core/modules/catalog/queries/related'
import { price } from '@vue-storefront/core/filters/price'
import MGuidedCheckoutStep from 'theme/components/molecules/guided-checkout/m-guided-checkout-step'
import { ModalList } from 'theme/store/ui/modals'
import {
  getAccessoryUnitPrice,
  getRelatedAccessorySkus,
  groupAccessoryProducts
} from 'theme/helpers/guided-checkout'

export default {
  name: 'MGcStepAccessories',
  components: {
    MGuidedCheckoutStep,
    SfImage
  },
  props: {
    stepNumber: {
      type: [Number, String],
      default: 5
    },
    product: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isLoading: false,
      accessoryProducts: [],
      categoriesById: {},
      activeGroupId: null,
      // sku -> qty (0 means not selected)
      qtyBySku: {}
    }
  },
  computed: {
    groupedProducts () {
      return groupAccessoryProducts(this.accessoryProducts, this.categoriesById)
    },
    activeGroup () {
      if (!this.groupedProducts.length) return null
      return this.groupedProducts.find(group => group.id === this.activeGroupId) || this.groupedProducts[0]
    },
    visibleProducts () {
      return (this.activeGroup && this.activeGroup.products) ? this.activeGroup.products : []
    }
  },
  watch: {
    product: {
      immediate: true,
      handler () {
        this.loadAccessories()
      }
    },
    groupedProducts (groups) {
      if (!groups.length) {
        this.activeGroupId = null
        return
      }
      if (!groups.some(group => group.id === this.activeGroupId)) {
        this.activeGroupId = groups[0].id
      }
    },
    value: {
      immediate: true,
      handler (items) {
        const next = {}
        ;(items || []).forEach(item => {
          if (item && item.product && item.product.sku) {
            const qty = Number(item.qty) || 0
            if (qty > 0) {
              next[item.product.sku] = qty
            }
          }
        })
        this.qtyBySku = next
      }
    }
  },
  methods: {
    ...mapActions('ui', {
      openModal: 'openModal'
    }),
    getThumbnailForProduct,
    formatUnitPrice (product) {
      return price(getAccessoryUnitPrice(product))
    },
    openProductDetails (product) {
      if (!product) return
      this.openModal({ name: ModalList.DataSheet, payload: product })
    },
    getQty (sku) {
      const qty = this.qtyBySku[sku]
      return qty > 0 ? qty : 0
    },
    getGroupSelectedCount (group) {
      if (!group || !group.products) return 0
      return group.products.reduce((count, item) => {
        return count + (this.getQty(item.sku) > 0 ? 1 : 0)
      }, 0)
    },
    async loadAccessories () {
      const skus = getRelatedAccessorySkus(this.product)
      if (!skus.length) {
        this.accessoryProducts = []
        this.categoriesById = {}
        this.activeGroupId = null
        return
      }

      this.isLoading = true
      try {
        const query = prepareRelatedQuery('sku', skus)
        query.applyFilter({ key: 'type_id', value: { neq: 'virtual' } })

        const response = await this.$store.dispatch('product/findProducts', {
          query,
          size: Math.max(skus.length, 20),
          prefetchGroupProducts: false,
          updateState: false
        })

        const items = (response && response.items) ? response.items.slice() : []
        items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' }))

        this.accessoryProducts = items
        await this.loadCategoriesForProducts(items)
      } finally {
        this.isLoading = false
      }
    },
    async loadCategoriesForProducts (products) {
      const ids = Array.from(new Set(
        (products || [])
          .flatMap(product => product.category_ids || [])
          .map(id => Number(id))
          .filter(id => !isNaN(id))
      ))

      if (!ids.length) {
        this.categoriesById = {}
        return
      }

      try {
        const response = await this.$store.dispatch('category/list', {
          key: 'id',
          value: ids,
          onlyActive: false,
          size: Math.max(ids.length, 50),
          updateState: false,
          skipCache: true,
          includeFields: ['id', 'name', 'parent_id', 'level', 'path', 'position']
        })

        const next = {}
        ;(response && response.items ? response.items : []).forEach(category => {
          if (category && category.id != null) {
            next[category.id] = category
            next[String(category.id)] = category
          }
        })
        this.categoriesById = next
      } catch (e) {
        this.categoriesById = {}
      }
    },
    changeQty (product, delta) {
      this.setQty(product, this.getQty(product.sku) + delta)
    },
    setQty (product, rawQty) {
      const qty = Math.max(0, parseInt(rawQty, 10) || 0)
      const next = { ...this.qtyBySku }

      if (qty > 0) {
        next[product.sku] = qty
      } else {
        delete next[product.sku]
      }

      this.qtyBySku = next
      this.emitSelection()
    },
    emitSelection () {
      const selected = Object.keys(this.qtyBySku).map(sku => {
        const product = this.accessoryProducts.find(item => item.sku === sku)
        const qty = this.qtyBySku[sku]
        if (!product || !(qty > 0)) return null
        return {
          product,
          qty
        }
      }).filter(Boolean)

      this.$emit('input', selected)
      this.$emit('changed', selected)
    }
  }
}
</script>

<style lang="scss" scoped>
.m-gc-step-accessories {
  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__filter {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    height: auto;
    min-height: 36px;
    padding: 0.4rem 0.75rem;
    border: 1px solid var(--c-light);
    border-radius: 999px;
    background: var(--c-white);
    cursor: pointer;
    white-space: nowrap;
    transition: var(--gc-transition);

    &--active {
      border-color: var(--c-primary);
      color: var(--c-primary);
      font-weight: 700;
    }
  }

  &__filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 999px;
    background: var(--c-primary);
    color: var(--c-white);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
  }

  &__status {
    margin: 0;
    color: var(--c-dark-variant);
    line-height: 1.4;
  }

  &__items {
    display: grid;
    gap: 0.5rem;
  }

  &__item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--c-light);
    border-radius: 4px;
    background: var(--c-white);
    transition: var(--gc-transition);

    &--active {
      border-color: var(--c-primary);
      box-shadow: 0 0 0 1px var(--c-primary);
    }
  }

  &__item-main {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex: 1 1 220px;
    min-width: 0;
  }

  ::v-deep .sf-image {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    max-width: 56px;
    overflow: hidden;
    border-radius: 4px;
    background: var(--c-light);

    img {
      width: 56px;
      height: 56px;
      object-fit: cover;
    }
  }

  &__item-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  &__item-title-row {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
  }

  &__item-name {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    text-align: left;
    line-height: 1.25;
    font-weight: 700;
    font-size: inherit;
    color: inherit;
    white-space: normal;
    overflow-wrap: anywhere;
    text-decoration: none;
    cursor: pointer;
  }

  &__item-info {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 1.25em; // same line box as the product name
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    cursor: pointer;

    img {
      display: block;
      width: 14px;
      height: 14px;
    }
  }

  &__item-price {
    display: block;
    color: var(--c-dark-variant);
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25;
  }

  &__qty {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    margin-left: auto;
    border: 1px solid var(--c-light);
    border-radius: 4px;
    overflow: hidden;
    background: var(--c-white);
  }

  &__qty-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: var(--c-light);
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__qty-input {
    width: 52px;
    height: 40px;
    padding: 0;
    border: 0;
    border-left: 1px solid var(--c-light);
    border-right: 1px solid var(--c-light);
    border-radius: 0;
    text-align: center;
    font-weight: 700;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
